/**
 * OpenAI Ads measurement pixel (Pixel ID EzHZJn4t2gChvP4ABxMqpi).
 *
 * Loaded only when the visitor granted ads-measurement permission AND the page is a
 * public/conversion page. Never on course content, documents or other signed-in pages.
 * The SDK sends nothing by itself; events are sent only through measure() below, always
 * with the server's event id, so browser and server reports deduplicate.
 * Debug logging stays off in production (REACT_APP_ADS_PIXEL_DEBUG=true only off app.ocesha.ai).
 */
import { clearPixelCookies } from "./adsConsent";

/**
 * Master switch: REACT_APP_ADS_TRACKING_ENABLED=Yes turns ChatGPT Ads tracking on (live app.ocesha.ai).
 * Missing or any other value = completely off (QA podcastclip.ocesha.ai): no pixel, no consent
 * prompt, no attribution kept, nothing sent to /api/ads, no ads data in signup requests.
 */
export const ADS_TRACKING_ENABLED =
  ["yes", "true", "1"].includes(String(process.env.REACT_APP_ADS_TRACKING_ENABLED || "").trim().toLowerCase());

export const PIXEL_ID = process.env.REACT_APP_ADS_PIXEL_ID || "EzHZJn4t2gChvP4ABxMqpi";
const SDK_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";

// Pages where the pixel may run (exact paths). Everything else - dashboards, courses,
// blogs, documents, settings - never loads it.
const PIXEL_PATHS = new Set(["/", "/signup", "/signin", "/verify", "/pricing", "/success",
  "/podcast", "/agency", "/small-business", "/aboutus", "/contactus"]);

export const isPixelPage = (pathname: string) => PIXEL_PATHS.has((pathname || "/").replace(/\/+$/, "") || "/");

declare global {
  interface Window { oaiq?: any; }
}

let loaded = false;
let initialized = false;

const loadSdk = () => {
  if (loaded) return;
  loaded = true;
  (function (w: any, d: Document, s: string, u: string) {
    if (w.oaiq) return;
    const q: any = function () { q.q.push(arguments); };
    q.q = [];
    w.oaiq = q;
    const js = d.createElement(s) as HTMLScriptElement;
    js.async = true;
    js.src = u;
    const f = d.getElementsByTagName(s)[0];
    f.parentNode!.insertBefore(js, f);
  })(window, document, "script", SDK_URL);
};

const debugEnabled = () =>
  process.env.REACT_APP_ADS_PIXEL_DEBUG === "true" && window.location.hostname !== "app.ocesha.ai";

/** Start the pixel (permission granted + allowed page). `user` = hashed ids from the server. */
export const startPixel = (user?: Record<string, string>) => {
  if (initialized || !ADS_TRACKING_ENABLED) return;
  loadSdk();
  const config: any = { pixelId: PIXEL_ID };
  if (debugEnabled()) config.debug = true;
  if (user && Object.keys(user).length) config.user = user;
  window.oaiq("init", config);
  window.oaiq("consent", true);           // only reached with the visitor's permission
  initialized = true;
};

/** Rejection / withdrawal: stop the pixel and delete its cookies. */
export const stopPixel = () => {
  if (window.oaiq) window.oaiq("consent", false);   // SDK removes __oppref / __obref
  clearPixelCookies();
  initialized = false;                               // re-granting starts it again
};

export const isPixelStarted = () => initialized;

export const measure = (eventName: string, data: Record<string, any>, eventId: string) => {
  if (!initialized || !window.oaiq) return;
  window.oaiq("measure", eventName, data, { event_id: eventId });
};
